import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  IconButton,
  Chip,
  Toolbar,
  Select,
  MenuItem,
  useTheme,
  alpha,
  useMediaQuery,
  Collapse,
} from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { Menu as MenuIcon } from '@mui/icons-material';
import { theme as appTheme } from '../../theme';
import { useUserRole, UserRole } from '../../contexts/UserRoleContext';
import { MenuItemType, useNavItems } from './NavItems';

const ROLE_LABELS: Record<UserRole, string> = {
  voter: 'Voter',
  journalist: 'Press',
  analyst: 'Analyst',
};
const ROLE_COLORS: Record<UserRole, string> = {
  voter: appTheme.palette.success.main,
  journalist: appTheme.palette.secondary.main,
  analyst: appTheme.palette.info.main,
};

interface AppSidebarProps {
  sidebarCollapsed: boolean;
  setSidebarCollapsed: (collapsed: boolean) => void;
  onDrawerToggle: () => void;
}

export const AppSidebar: React.FC<AppSidebarProps> = ({
  sidebarCollapsed,
  setSidebarCollapsed,
  onDrawerToggle,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { role, setRole } = useUserRole();
  const menuItems = useNavItems();

  const [openGroups, setOpenGroups] = React.useState<Record<string, boolean>>({
    'Election Info': true,
    'Live Center': true,
    Analysis: true,
  });

  const handleGroupClick = (text: string) => {
    if (sidebarCollapsed) {
      setSidebarCollapsed(false);
      setTimeout(() => {
        setOpenGroups((prev) => ({ ...prev, [text]: true }));
      }, 150);
    } else {
      setOpenGroups((prev) => ({ ...prev, [text]: !prev[text] }));
    }
  };

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  const renderMenuItem = (item: MenuItemType, index: number, nested = false): React.ReactNode => {
    const isSelected = item.path ? location.pathname === item.path : false;
    const hasChildren = item.children && item.children.length > 0;
    const isGroupOpen = openGroups[item.text];

    return (
      <React.Fragment key={item.text + index}>
        <ListItemButton
          onClick={() => {
            if (hasChildren) {
              handleGroupClick(item.text);
            } else if (item.path) {
              handleNavigation(item.path);
            }
          }}
          sx={{
            borderRadius: 1.5,
            mb: 0.25,
            justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
            px: sidebarCollapsed ? 1 : nested ? 3 : 1.5,
            py: nested ? 0.6 : 0.75,
            backgroundColor: isSelected ? alpha(theme.palette.success.main, 0.15) : 'transparent',
            color: isSelected ? theme.palette.success.main : 'rgba(255,255,255,0.57)',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.06)',
              color: 'rgba(255,255,255,0.9)',
            },
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: sidebarCollapsed ? 'auto' : 30,
              color: isSelected ? theme.palette.success.main : 'white',
              mr: sidebarCollapsed ? 0 : 1.25,
              '& .MuiSvgIcon-root': { fontSize: nested ? '1.1rem' : '1.2rem' },
            }}
          >
            {item.icon}
          </ListItemIcon>
          {!sidebarCollapsed && (
            <>
              <ListItemText
                primary={item.text}
                primaryTypographyProps={{
                  fontSize: '0.875rem',
                  fontWeight: isSelected ? 600 : hasChildren ? 500 : 400,
                  color: isSelected
                    ? theme.palette.success.main
                    : hasChildren
                    ? 'rgba(255,255,255,0.75)'
                    : 'rgba(255,255,255,0.57)',
                  letterSpacing: hasChildren && !nested ? '0.01em' : 0,
                }}
              />
              {item.badge && (
                <Chip
                  label={item.badge}
                  size="small"
                  color="secondary"
                  sx={{ height: 20, ml: 1, '& .MuiChip-label': { fontSize: '0.75rem', px: 1 } }}
                />
              )}
              {hasChildren &&
                (isGroupOpen ? (
                  <ExpandLess fontSize="small" sx={{ color: 'info.main', fontSize: '1rem' }} />
                ) : (
                  <ExpandMore fontSize="small" sx={{ color: 'info.main', fontSize: '1rem' }} />
                ))}
            </>
          )}
        </ListItemButton>

        {hasChildren && !sidebarCollapsed && (
          <Collapse in={isGroupOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.children!.map((child, idx) => renderMenuItem(child, idx, true))}
            </List>
          </Collapse>
        )}
      </React.Fragment>
    );
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'primary.main' }}>
      <Toolbar
        sx={{
          bgcolor: 'primary.main',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: sidebarCollapsed ? 'center' : 'space-between',
          px: sidebarCollapsed ? 1 : 2,
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          minHeight: 64,
        }}
      >
        {!sidebarCollapsed && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: 1.5,
                bgcolor: 'success.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography sx={{ color: 'white', fontWeight: 800, fontSize: '0.75rem', lineHeight: 1 }}>
                E
              </Typography>
            </Box>
            <Typography
              variant="subtitle1"
              noWrap
              sx={{ fontWeight: 700, color: 'white', letterSpacing: '-0.01em' }}
            >
              Electure
            </Typography>
          </Box>
        )}
        {!isMobile && (
          <IconButton
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            sx={{
              color: 'info.main',
              '&:hover': { color: 'white', bgcolor: 'rgba(255,255,255,0.08)' },
            }}
            size="small"
          >
            <MenuIcon fontSize="small" />
          </IconButton>
        )}
        {isMobile && (
          <IconButton onClick={onDrawerToggle} sx={{ color: 'info.main' }}>
            <MenuIcon />
          </IconButton>
        )}
      </Toolbar>

      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          '&::-webkit-scrollbar': { width: 4 },
          '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 2 },
        }}
      >
        <List sx={{ px: 1, py: 1.5 }}>
          {menuItems.map((item, index) => renderMenuItem(item, index))}
        </List>
      </Box>

      {/* Role switcher — bottom of sidebar */}
      {!sidebarCollapsed && (
        <Box sx={{ px: 1.5, py: 1.5, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <Typography
            sx={{
              fontSize: '0.75rem',
              fontWeight: 700,
              letterSpacing: '0.08em',
              color: 'rgba(255,255,255,0.4)',
              textTransform: 'uppercase',
              mb: 0.75,
            }}
          >
            {t('sidebar.viewAs')}
          </Typography>
          <Select
            value={role}
            onChange={(e) => setRole(e.target.value as UserRole)}
            size="small"
            fullWidth
            sx={{
              fontSize: '0.875rem',
              color: '#fff',
              '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.15)' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: ROLE_COLORS[role] },
              '.MuiSvgIcon-root': { color: 'rgba(255,255,255,0.5)' },
              bgcolor: 'rgba(255,255,255,0.06)',
            }}
          >
            {(['voter', 'journalist', 'analyst'] as UserRole[]).map((r) => (
              <MenuItem key={r} value={r} sx={{ fontSize: '0.875rem' }}>
                {ROLE_LABELS[r]}
              </MenuItem>
            ))}
          </Select>
        </Box>
      )}
    </Box>
  );
};
